'use client';

import Link from 'next/link';
import * as m from 'framer-motion/m';
import { FiCheck } from 'react-icons/fi';
import { useState, useEffect } from 'react';
import { useCart } from '../lib/CartContext';
import { useTheme } from '../lib/ThemeContext';
import customToast from '../lib/toastUtils';
import { HOME_H2_CLASS } from '../lib/typography';

export type SEOPricingCopy = {
  sectionTitle: string;
  sectionSubtitle: string;
  packageLabel: string;
  starterTitle: string;
  monthlyLabel: string;
  perMonth: string;
  starterSubtitle: string;
  includedLabel: string;
  starterFeatures: string[];
  addToCart: string;
  toastAdded: string;
  toastExists: string;
  advancedTitle: string;
  enterpriseLabel: string;
  customPricingLabel: string;
  advancedSubtitle: string;
  everythingPlusLabel: string;
  advancedFeatures: string[];
  getQuote: string;
};

const DEFAULT_COPY: SEOPricingCopy = {
  sectionTitle: 'SEO Plans',
  sectionSubtitle: 'Choose the plan that fits your business goals.',
  packageLabel: 'SEO PACKAGE',
  starterTitle: 'Starter',
  monthlyLabel: 'Monthly',
  perMonth: '/month',
  starterSubtitle: 'Perfect for small to medium businesses',
  includedLabel: "What's Included",
  starterFeatures: [
    'Keyword research & strategy',
    'On-page SEO optimization',
    'Technical SEO audit',
    'Monthly performance report',
  ],
  addToCart: 'Add to Cart',
  toastAdded: 'SEO Starter added to cart!',
  toastExists: 'SEO Starter is already in your cart',
  advancedTitle: 'Advanced SEO',
  enterpriseLabel: 'Enterprise',
  customPricingLabel: 'Custom Pricing',
  advancedSubtitle: 'Tailored solutions for enterprise needs',
  everythingPlusLabel: 'Everything in Starter, plus',
  advancedFeatures: [
    'Custom SEO strategy',
    'Dedicated account manager',
    'Priority support',
    'Advanced reporting & analytics',
    'Competitor analysis',
  ],
  getQuote: 'Get a Quote',
};

type Props = {
  seoStarterPrice: { amount: string; symbol: string } | null;
  seoStarterPriceId: string | null;
  contactUrl: string;
  market?: 'eu' | 'us' | 'gb';
  copy?: Partial<SEOPricingCopy>;
};

export default function SEOPricingCardsSection({
  seoStarterPrice,
  seoStarterPriceId,
  contactUrl,
  market = 'eu',
  copy: copyOverride,
}: Props) {
  const isUS = market === 'us';
  const copy: SEOPricingCopy = { ...DEFAULT_COPY, ...copyOverride };
  const { addProduct, cartProducts } = useCart();
  const { theme } = useTheme();
  const planTitlePriceClass = HOME_H2_CLASS;
  const [isCardsHovered, setIsCardsHovered] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const darkCardShadow = theme === 'dark' ? { boxShadow: isCardsHovered ? '0 12px 48px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.3)' } : undefined;

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsDesktop(mq.matches);
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <m.section
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className='max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 mb-8'
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div className='text-center mb-4 sm:mb-6 lg:mb-8'>
        <h2 className='text-[18px] leading-[24px] font-normal text-gray-900 dark:text-white mb-2 md:text-[20px] md:leading-[26px] md:font-normal'>
          {copy.sectionTitle}
        </h2>
        <p className='text-sm sm:text-base md:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed'>
          {copy.sectionSubtitle}
        </p>
      </div>

      <div
        className='relative flex flex-col lg:flex-row justify-center items-center gap-8 lg:gap-0 w-full mx-auto min-h-0 lg:min-h-[600px] py-2 lg:py-0'
        onMouseEnter={() => setIsCardsHovered(true)}
        onMouseLeave={() => setIsCardsHovered(false)}
      >
        {/* Starter Plan - Glass Card (Front/Overlapping) */}
        <m.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          animate={{
            x: isDesktop ? (isCardsHovered ? -490 : -380) : 0,
            rotate: isDesktop ? (isCardsHovered ? 0 : -8) : 0,
          }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          className='relative lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-full max-w-[380px] min-h-[480px] lg:h-[540px] rounded-3xl p-7 bg-white dark:bg-white/5 dark:backdrop-blur-xl border border-gray-200 dark:border-white/20 shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] transition-shadow duration-300 z-10 flex flex-col'
          style={darkCardShadow}
        >
          <div className='flex items-start justify-between mb-4'>
            <div>
              <h3 className='text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold mb-1'>
                {copy.packageLabel}
              </h3>
              <h2 className={planTitlePriceClass}>
                {copy.starterTitle}
              </h2>
            </div>
            <div className='flex items-center justify-center px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/20'>
              <span className='text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider leading-none'>
                {copy.monthlyLabel}
              </span>
            </div>
          </div>

          <div className='mb-5'>
            <div className='flex items-baseline gap-1'>
                <span className={planTitlePriceClass}>
                {seoStarterPrice ? (
                  <>
                    {seoStarterPrice.symbol}
                    {parseFloat(seoStarterPrice.amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </>
                ) : (
                  <>{isUS ? '$' : '€'}—</>
                )}
              </span>
              <span className='text-sm text-gray-500 dark:text-gray-400'>{copy.perMonth}</span>
            </div>
            <p className='text-xs text-gray-600 dark:text-gray-400 mt-1.5'>
              {copy.starterSubtitle}
            </p>
          </div>

          <div className='h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/20 to-transparent mb-5' />

          <div className='space-y-3 mb-5 flex-grow'>
            <div className='text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-2'>
              {copy.includedLabel}
            </div>
            {copy.starterFeatures.map((feature, i) => (
              <div key={i} className='flex items-start gap-2'>
                <div className='mt-0.5 w-3.5 h-3.5 rounded-full bg-gray-200 dark:bg-white/20 flex items-center justify-center flex-shrink-0'>
                  <FiCheck className='w-2 h-2 text-gray-700 dark:text-white' strokeWidth={3} />
                </div>
                <span className='text-[13px] text-gray-700 dark:text-gray-300'>{feature}</span>
              </div>
            ))}
          </div>

          <button
            type='button'
            onClick={() => {
              if (!seoStarterPriceId) return;
              const alreadyInCart = cartProducts.some(
                (p) => p.id === 'seo-starter' && p.billingCycle === 'monthly' && p.mode === 'subscription'
              );
              if (alreadyInCart) {
                customToast.info(copy.toastExists, 'seo-starter-exists');
                return;
              }
              addProduct({
                id: 'seo-starter',
                stripePriceId: seoStarterPriceId,
                price: seoStarterPrice?.amount || '0.00',
                title: 'SEO Starter',
                mode: 'subscription',
                billingCycle: 'monthly',
              });
              customToast.success(copy.toastAdded, 'seo-starter-added');
            }}
            disabled={!seoStarterPriceId}
            className='block w-full py-3 rounded-xl text-[14px] sm:text-[16px] leading-[20px] sm:leading-[24px] font-semibold not-italic text-center transition-all duration-300 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-800 dark:text-white border border-gray-200 dark:border-white/20 hover:border-gray-300 dark:hover:border-white/30 mt-auto disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {copy.addToCart}
          </button>
        </m.div>

        {/* Advanced SEO Plan - Glass Card (Back) */}
        <m.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          animate={{
            x: isDesktop ? (isCardsHovered ? 30 : 20) : 0,
            rotate: isDesktop ? (isCardsHovered ? 0 : -8) : 0,
          }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          className='relative lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-full max-w-[380px] min-h-[480px] lg:h-[540px] rounded-3xl p-7 bg-white dark:bg-white/5 dark:backdrop-blur-xl border border-gray-200 dark:border-white/20 shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] transition-shadow duration-300 z-0 flex flex-col'
          style={darkCardShadow}
        >
          <div className='flex items-start justify-between mb-4'>
            <div>
              <h3 className='text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-400 font-semibold mb-1'>
                {copy.packageLabel}
              </h3>
              <h2 className={planTitlePriceClass}>
                {copy.advancedTitle}
              </h2>
            </div>
            <div className='flex items-center justify-center px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/20'>
              <span className='text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider leading-none'>
                {copy.enterpriseLabel}
              </span>
            </div>
          </div>

          <div className='mb-5'>
            <div className='flex items-baseline gap-2'>
              <span className={planTitlePriceClass}>
                {copy.customPricingLabel}
              </span>
            </div>
            <p className='text-xs text-gray-600 dark:text-gray-300 mt-1.5'>
              {copy.advancedSubtitle}
            </p>
          </div>

          <div className='h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/20 to-transparent mb-5' />

          <div className='space-y-3 mb-6 flex-grow'>
            <div className='text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2'>
              {copy.everythingPlusLabel}
            </div>
            {copy.advancedFeatures.map((feature, i) => (
              <div key={i} className='flex items-start gap-2'>
                <div className='mt-0.5 w-4 h-4 rounded-full bg-gray-200 dark:bg-white/20 flex items-center justify-center flex-shrink-0'>
                  <FiCheck className='w-2.5 h-2.5 text-gray-700 dark:text-white' strokeWidth={3} />
                </div>
                <span className='text-sm text-gray-700 dark:text-gray-300'>{feature}</span>
              </div>
            ))}
          </div>

          <Link
            href={contactUrl}
            className='block w-full py-3 rounded-xl text-[14px] sm:text-[16px] leading-[20px] sm:leading-[24px] font-semibold not-italic text-center transition-all duration-300 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-800 dark:text-white border border-gray-200 dark:border-white/20 hover:border-gray-300 dark:hover:border-white/30 mt-auto'
          >
            {copy.getQuote}
          </Link>
        </m.div>
      </div>
    </m.section>
  );
}
